import type { ProfileBookmark } from '~/src/hooks/useFetchProfileData'
import type { UnifiedRecentItem } from '~/src/hooks/useFetchRecents'
import type { LibraryBottle } from '~/src/hooks/useLibraryBottles'

/** The header item's name, in one place so it is one edit to change. */
export const LIBRARY_LABEL = 'Library'

/** The Library's tabs, in the order both menus draw them. */
export type LibraryTabId = 'recents' | 'saves' | 'bottles'
export const LIBRARY_TABS: LibraryTabId[] = ['recents', 'saves', 'bottles']
export const LIBRARY_TAB_LABELS: Record<LibraryTabId, string> = {
  recents: 'Recents',
  saves: 'Saves',
  bottles: 'Bottles',
}

/** The shopping categories Recents and Saves filter by. */
export type LibraryCategory = 'furniture' | 'fashion' | 'fragrance'
export const LIBRARY_CATEGORIES: LibraryCategory[] = [
  'furniture',
  'fashion',
  'fragrance',
]

/**
 * The Recents and Saves chips: the categories, then Other, for what's filed
 * under none of them (`/api/recents?category=other`).
 */
export type LibraryCategoryFilter = LibraryCategory | 'other'
export const LIBRARY_CATEGORY_FILTERS: LibraryCategoryFilter[] = [
  ...LIBRARY_CATEGORIES,
  'other',
]

/** The bottle lists Bottles filters by, as the My bottles card's filters. */
export type BottleListId = 'loved' | 'owned' | 'wanted'
export const BOTTLE_LISTS: BottleListId[] = ['loved', 'owned', 'wanted']

export type LibraryFilter = LibraryCategoryFilter | BottleListId
export const LIBRARY_FILTER_LABELS: Record<LibraryFilter, string> = {
  furniture: 'Furniture',
  fashion: 'Fashion',
  fragrance: 'Fragrances',
  other: 'Other',
  loved: 'Love',
  owned: 'Own',
  wanted: 'Want',
}
export const LIBRARY_TAB_FILTERS: Record<LibraryTabId, LibraryFilter[]> = {
  recents: LIBRARY_CATEGORY_FILTERS,
  saves: LIBRARY_CATEGORY_FILTERS,
  bottles: BOTTLE_LISTS,
}

/** One row in either menu: every tab lists the same way. */
export interface LibraryEntry {
  key: string
  href: string
  title: string
  subtitle?: string | null
  image?: string | null
  /** No picture: a bottle icon rather than a blank square. */
  fragrance?: boolean
  /** Opens in a new tab (a save, through our redirect). */
  external?: boolean
  /** What the tab's filters match: its category, or the bottle lists it's on. */
  tags: LibraryFilter[]
  /** A bottle's lists, drawn as their icons after its brand. */
  lists?: BottleListId[]
  /** What the search box matches: its name, plus the brand or store. */
  search: string
}

/** A tab's count: "20", or "20+" when there are more than were read. */
export const libraryCount = (n: number, more = false) =>
  more ? `${n}+` : String(n)

const isCategory = (value: unknown): value is LibraryCategory =>
  LIBRARY_CATEGORIES.includes(value as LibraryCategory)

/**
 * The chip a recent or save is filed under: its stored `category`, else Other.
 * Only the stored category, as the api's `?category=` filter reads it, so a
 * list filtered here and one filtered there agree. (A fragrance chat with no
 * stored category yet still gets the bottle icon, but files under Other.)
 */
const categoryOf = (item: { category?: string | null }): LibraryFilter[] =>
  isCategory(item.category) ? [item.category] : ['other']

const RELATIVE = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })

/** "2 hours ago", "yesterday", "3 weeks ago". */
export const timeAgo = (when: Date | string | undefined, now = Date.now()) => {
  const at = when ? new Date(when).getTime() : NaN
  if (!Number.isFinite(at)) return null
  const minutes = Math.round((at - now) / 60000)
  if (Math.abs(minutes) < 60) return RELATIVE.format(minutes, 'minute')
  const hours = Math.round(minutes / 60)
  if (Math.abs(hours) < 24) return RELATIVE.format(hours, 'hour')
  const days = Math.round(hours / 24)
  if (Math.abs(days) < 7) return RELATIVE.format(days, 'day')
  const weeks = Math.round(days / 7)
  if (Math.abs(weeks) < 5) return RELATIVE.format(weeks, 'week')
  return RELATIVE.format(Math.round(days / 30), 'month')
}

export const recentEntry = (
  recent: UnifiedRecentItem,
  index: number
): LibraryEntry => ({
  key: recent?.taskId || recent?.id || recent?.hash || String(index),
  href: recent?.url,
  title: recent?.title || recent?.summary || 'Untitled search',
  subtitle: timeAgo(recent?.timestamp),
  image: recent?.image,
  fragrance: recent?.fragrance,
  tags: categoryOf(recent),
  search: recent?.title || recent?.summary || '',
})

/** "$129" (whole) or "$12.50"; null without a price. */
export const formatPrice = (pricing: ProfileBookmark['pricing']) => {
  const price = pricing?.price
  if (price == null || !Number.isFinite(price)) return null
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: pricing?.currency || 'USD',
      maximumFractionDigits: Number.isInteger(price) ? 0 : 2,
    }).format(price)
  } catch {
    return null
  }
}

/** A save; `href` is the redirect link the caller builds (it needs UTM params). */
export const saveEntry = (
  save: ProfileBookmark,
  index: number,
  href: string
): LibraryEntry => ({
  key: `${save.cartItemId}-${index}`,
  href,
  title: save.title || 'Saved item',
  subtitle:
    [save.source, formatPrice(save.pricing)].filter(Boolean).join(' · ') ||
    null,
  image: save.image,
  external: true,
  tags: categoryOf(save),
  search: [save.title, save.source].filter(Boolean).join(' '),
})

/**
 * Every bottle once, loved first, then owned, then wanted, each tagged with
 * the lists it's on ("Dior · Love, Own").
 */
export const bottleEntries = (lists: Record<BottleListId, LibraryBottle[]>) => {
  const byId = new Map<string, { bottle: LibraryBottle; on: BottleListId[] }>()
  for (const list of BOTTLE_LISTS) {
    for (const bottle of lists[list]) {
      const seen = byId.get(bottle.entityId)
      if (seen) seen.on.push(list)
      else byId.set(bottle.entityId, { bottle, on: [list] })
    }
  }
  return [...byId.values()].map(
    ({ bottle, on }): LibraryEntry => ({
      key: bottle.entityId,
      href: bottle.href,
      title: bottle.name,
      subtitle: bottle.brand,
      image: bottle.imageUrl,
      fragrance: true,
      tags: on,
      lists: BOTTLE_LISTS.filter((l) => on.includes(l)),
      search: [bottle.name, bottle.brand].filter(Boolean).join(' '),
    })
  )
}

export const matchesFilter = (
  entry: LibraryEntry,
  filter: LibraryFilter | null
) => !filter || entry.tags.includes(filter)
